
import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as Firestore from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Recipe, Role, HouseholdRole, Language, Review, DayOfWeek, MealType, Ingredient, InstructionStep, User, Chef, UserMealPreferences } from '../types';
import XIcon from './icons/XIcon';
import HeartIcon from './icons/HeartIcon';
import MeatIcon from './icons/MeatIcon';
import HamIcon from './icons/HamIcon';
import LeafIcon from './icons/LeafIcon';
import PencilIcon from './icons/PencilIcon';
import ClockIcon from './icons/ClockIcon';
import UsersIcon from './icons/UsersIcon';
import CalendarPlusIcon from './icons/CalendarPlusIcon';
import CartIcon from './icons/CartIcon';
import StarRating from './StarRating';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ShareIcon from './icons/ShareIcon';
import TrashIcon from './icons/TrashIcon';
import SmartUnitCapsule from './SmartUnitCapsule';
import { getRecipeGalleryImageUrls } from '../utils/recipeMedia';
import RecipeImageGallery from './RecipeImageGallery';
import RecipeExportModal from './RecipeExportModal';
import ConfirmationModal from './ConfirmationModal';
import { getTranslation, isRTL } from '../utils/translations';
import CookingQuantityCapsule from './CookingQuantityCapsule';
import { matchIngredientsInStep } from '../utils/ingredientMatching';
import NutritionFactsPanel from './NutritionFactsPanel';
import { DAYS_OF_WEEK } from '../types';
import { DIET_TYPE_CONFIG, normalizeDietTypes } from '../utils/dietTypes';
import {
    estimateLifestyleTags,
    LIFESTYLE_TAG_CONFIG,
    mergeEstimatedLifestyleTags,
    normalizeLifestyleTags,
} from '../utils/lifestyleTags';
import { getChefDisplayName, isChefActive } from '../utils/chefDisplay';
import { sanitizeExternalHref } from '../utils/safeUrl';
import ChefAvatar from './ChefAvatar';
import { isFavoriteRecipe } from '../utils/userMealPreferences';
import { buildUserLookup, resolveRecipeAuthorName } from '../utils/userDisplay';
import ChefHatIcon from './icons/ChefHatIcon';
import CopyButton from './CopyButton';

interface RecipeDetailsModalProps {
    recipe: Recipe;
    role: Role; 
    householdRole: HouseholdRole; 
    currentUserId: string;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onOpenAddMeal?: () => void;
    onAddIngredientsToCart?: (ingredients: Ingredient[], recipeName: string) => Promise<void>;
    onGenerateRecipeShoppingList?: (recipe: Recipe, sectionName?: string) => void;
    language?: Language;
    orderedDays?: DayOfWeek[];
    visibleMealTypes?: MealType[];
    activeHouseholdId?: string;
    allUsers?: User[];
    viewingUser?: User | null;
    chefs?: Chef[];
    onViewChef?: (chefId: string) => void;
    userMealPreferences?: UserMealPreferences | null;
    onToggleFavoriteRecipe?: (recipeId: string) => Promise<void>;
    onDuplicateForHousehold?: () => void;
    showDuplicateForHousehold?: boolean;
    guidedMode?: boolean;
}

const { doc, getDoc, runTransaction } = Firestore as any;

const getDomain = (url: string) => {
    try {
        const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
        return urlObj.hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

const RecipeDetailsModal: React.FC<RecipeDetailsModalProps> = ({ 
    recipe, role, householdRole, currentUserId, onClose, onEdit, onDelete, 
    onOpenAddMeal,
    onAddIngredientsToCart, onGenerateRecipeShoppingList, 
    language, orderedDays = DAYS_OF_WEEK, visibleMealTypes = ['Lunch', 'Dinner'],
    activeHouseholdId, allUsers = [], viewingUser = null, chefs = [], onViewChef,
    userMealPreferences = null, onToggleFavoriteRecipe,
    onDuplicateForHousehold, showDuplicateForHousehold = false,
    guidedMode = false,
}) => {
    const baseCanManage = role === 'superuser' || ['owner', 'admin', 'editor'].includes(householdRole);
    const canManage = role === 'superuser' || (baseCanManage && recipe.householdId === activeHouseholdId);
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    const [struckIngredients, setStruckIngredients] = useState<Set<string>>(new Set());
    const [collapsedIngSections, setCollapsedIngSections] = useState<Set<string>>(new Set());
    const [collapsedPrepSections, setCollapsedPrepSections] = useState<Set<string>>(new Set());
    const [userRating, setUserRating] = useState<number>(0);
    const [currentServings, setCurrentServings] = useState(recipe.servings || 1);
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

    // Selection Mode State
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIngredients, setSelectedIngredients] = useState<Set<string>>(new Set());
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isNutritionCollapsed, setIsNutritionCollapsed] = useState(false);

    const userLang = language || 'en';
    const sourceLang = recipe.originLanguage || 'en';
    const showOriginal = userLang === sourceLang;

    // Memoize displayRecipe to avoid object churn
    const displayRecipe = useMemo(() => {
        if (showOriginal) return recipe;
        return {
            ...recipe,
            name: recipe.englishTranslation?.name || recipe.name,
            instructions: recipe.englishTranslation?.instructions || recipe.instructions,
            ingredients: recipe.ingredients.map((ing, i) => ({
                ...ing,
                name: recipe.englishTranslation?.ingredients[i]?.name || ing.name,
                unit: recipe.englishTranslation?.ingredients[i]?.unit || ing.unit,
                section: recipe.englishTranslation?.ingredients[i]?.section || ing.section
            })),
            nutritionFactsPer100g: recipe.englishTranslation?.nutritionFactsPer100g || recipe.nutritionFactsPer100g,
        };
    }, [recipe, showOriginal]);

    const galleryImageUrls = useMemo(
        () => getRecipeGalleryImageUrls(recipe),
        [recipe.imageUrl, recipe.imageUrls],
    );

    const meatStats = useMemo(() => {
        let totalGrams = 0;
        const ratio = currentServings / (recipe.servings || 1);
        displayRecipe.ingredients.forEach(ing => {
            if (ing.isMeat) {
                let qty = (ing.quantity || 0) * ratio;
                const unit = ing.unit?.toLowerCase().trim();
                if (unit === 'kg' || unit === 'ק"ג') totalGrams += qty * 1000;
                else if (unit === 'g' || unit === 'גרם') totalGrams += qty;
                else totalGrams += qty * 200; // Guess
            }
        });
        
        return { totalGrams, isHeavy: totalGrams >= 800 };
    }, [displayRecipe.ingredients, currentServings, recipe.servings]);

    const authorName = useMemo(() => {
        const userLookup = buildUserLookup(allUsers, viewingUser);
        return resolveRecipeAuthorName(recipe, currentUserId, userLookup, {
            me: t('me'),
            anonymous: t('anonymous'),
        });
    }, [allUsers, viewingUser, recipe.ownerId, currentUserId, language]);

    const sourceDomain = useMemo(() => {
        const url = recipe.links?.[0];
        if (!url) return null;
        try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return getDomain(url) || null; }
    }, [recipe.links]);

    const creditedChef = useMemo(() => {
        if (!recipe.chefId) return null;
        const chef = chefs.find((c) => c.id === recipe.chefId);
        if (!chef || !isChefActive(chef)) return null;
        return chef;
    }, [recipe.chefId, chefs]);

    const recipeTags = useMemo(() => {
        const dietTypes = normalizeDietTypes(recipe.dietTypes, recipe.isKeto);
        const lifestyle = mergeEstimatedLifestyleTags(
            normalizeLifestyleTags(recipe.lifestyleTags),
            estimateLifestyleTags(recipe),
        );
        const lifestyleTags = LIFESTYLE_TAG_CONFIG.filter(({ key }) => lifestyle[key]);
        const dietaryTags = DIET_TYPE_CONFIG.filter(({ key }) => dietTypes[key]);
        return [...lifestyleTags, ...dietaryTags];
    }, [recipe]);

    const groupedIngredients = useMemo(() => {
        const groups: Record<string, Ingredient[]> = {};
        displayRecipe.ingredients.forEach(ing => {
            const section = ing.section || t('ingredients');
            if (!groups[section]) groups[section] = [];
            groups[section].push(ing);
        });
        return Object.entries(groups).map(([section, ings]) => {
            const sortedIngs = [...ings].sort((a, b) => {
                if (a.isMeat && !b.isMeat) return -1;
                if (!a.isMeat && b.isMeat) return 1;
                return a.name.localeCompare(b.name);
            });
            return [section, sortedIngs] as [string, Ingredient[]];
        });
    }, [displayRecipe.ingredients, t]);

    const groupedInstructions = useMemo(() => {
        const sections: { header: string; steps: InstructionStep[] }[] = [];
        let rawSteps: InstructionStep[] = [];
        
        if (typeof displayRecipe.instructions === 'string') {
            rawSteps = displayRecipe.instructions.split('\n')
                .map(s => s.trim())
                .filter(Boolean)
                .map(s => ({ id: Math.random().toString(), type: 'step' as const, content: s.replace(/^\d+[\.\)\-\s]+/, '').trim() }));
        } else {
            rawSteps = displayRecipe.instructions as InstructionStep[];
        }

        let currentSection: { header: string; steps: InstructionStep[] } | null = null;
        rawSteps.forEach(s => {
            if (s.type === 'header') {
                currentSection = { header: s.content, steps: [] };
                sections.push(currentSection);
            } else {
                if (!currentSection) {
                    currentSection = { header: t('prepTab'), steps: [] };
                    sections.push(currentSection);
                }
                currentSection.steps.push(s);
            }
        });
        return sections;
    }, [displayRecipe.instructions, t]);

    // Initial collapse setup - safe useEffect to avoid loops
    // Use a ref to track if initial setup is done to prevent re-running
    const hasInitializedSections = React.useRef(false);
    useEffect(() => {
        if (hasInitializedSections.current) return;
        
        if (groupedIngredients.length > 0) {
             setCollapsedIngSections(new Set(groupedIngredients.map(([name]) => name)));
        }
        if (groupedInstructions.length > 0) {
             const prepKeys = groupedInstructions.map(s => s.header);
             setCollapsedPrepSections(new Set(prepKeys.slice(1)));
        }
        hasInitializedSections.current = true;
    }, [groupedIngredients.length, groupedInstructions.length]);

    useEffect(() => {
        const fetchRating = async () => {
            const reviewId = `${recipe.id}_${currentUserId}`;
            try {
                const docSnap = await getDoc(doc(db, 'reviews', reviewId));
                if (docSnap.exists()) setUserRating((docSnap.data() as Review).rating);
            } catch (err) { console.error("Rating error", err); }
        };
        fetchRating();
    }, [recipe.id, currentUserId]);

    const handleRate = async (rating: number) => {
        try {
            const reviewId = `${recipe.id}_${currentUserId}`;
            const recipeRef = doc(db, 'recipes', recipe.id);
            const reviewRef = doc(db, 'reviews', reviewId);
            await runTransaction(db, async (transaction: any) => {
                const reviewDoc = await transaction.get(reviewRef);
                const recipeDoc = await transaction.get(recipeRef);
                if (!recipeDoc.exists()) throw "Recipe missing";
                const currentAvg = recipeDoc.data().averageRating || 0;
                const currentCount = recipeDoc.data().ratingCount || 0;
                let newAvg = currentAvg, newCount = currentCount, currentTotal = currentAvg * currentCount;
                if (reviewDoc.exists()) {
                    currentTotal = currentTotal - reviewDoc.data().rating + rating;
                    newAvg = currentTotal / currentCount;
                    transaction.update(reviewRef, { rating, updatedAt: new Date().toISOString() });
                } else {
                    currentTotal += rating;
                    newCount++;
                    newAvg = currentTotal / newCount;
                    transaction.set(reviewRef, { id: reviewId, recipeId: recipe.id, userId: currentUserId, rating, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
                }
                transaction.update(recipeRef, { averageRating: newAvg, ratingCount: newCount });
            });
            setUserRating(rating);
        } catch (e) { console.error(e); }
    };

    const toggleStrike = (name: string) => {
        setStruckIngredients(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const toggleSelection = (name: string) => {
        setSelectedIngredients(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const allIngredientNames = useMemo(() => displayRecipe.ingredients.map(ing => ing.name), [displayRecipe.ingredients]);
    const allIngredientsSelected = allIngredientNames.length > 0 && allIngredientNames.every(n => selectedIngredients.has(n));

    const toggleSelectAll = () => {
        setSelectedIngredients(prev => {
            if (allIngredientNames.length > 0 && allIngredientNames.every(n => prev.has(n))) return new Set();
            return new Set(allIngredientNames);
        });
    };

    const isSectionFullySelected = (ings: Ingredient[]) => ings.length > 0 && ings.every(ing => selectedIngredients.has(ing.name));

    const toggleSelectSection = (ings: Ingredient[]) => {
        const names = ings.map(ing => ing.name);
        setSelectedIngredients(prev => {
            const next = new Set(prev);
            if (names.every(n => next.has(n))) {
                names.forEach(n => next.delete(n));
            } else {
                names.forEach(n => next.add(n));
            }
            return next;
        });
    };

    const toggleSelectionMode = () => {
        if (isSelectionMode) {
            setIsSelectionMode(false);
            setSelectedIngredients(new Set());
        } else {
            setIsSelectionMode(true);
            setSelectedIngredients(new Set());
        }
    };

    const handleAddToCart = async () => {
        if (!onAddIngredientsToCart || selectedIngredients.size === 0) return;
        setIsAddingToCart(true);
        try {
            const selectedItems = displayRecipe.ingredients
                .filter(ing => selectedIngredients.has(ing.name))
                .map(ing => {
                    const scalingFactor = currentServings / (recipe.servings || 1);
                    return { ...ing, quantity: ing.quantity * scalingFactor };
                });
            
            await onAddIngredientsToCart(selectedItems, displayRecipe.name);
            
            setIsSelectionMode(false);
            setSelectedIngredients(new Set());
        } catch (error) {
            console.error("Failed to add to cart", error);
        } finally {
            setIsAddingToCart(false);
        }
    };

    const toggleIngSection = (name: string) => {
        setCollapsedIngSections(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const togglePrepSection = (name: string) => {
        setCollapsedPrepSections(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    // Stable composite key: section index + step index (avoids collisions & unstable random ids)
    const stepKey = (sectionIdx: number, stepIdx: number) => `${sectionIdx}-${stepIdx}`;

    const toggleStepComplete = (key: string) => {
        setCompletedSteps(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const getStepIngredients = (stepText: string) => matchIngredientsInStep(
        stepText,
        displayRecipe.ingredients,
        { englishIngredients: displayRecipe.englishTranslation?.ingredients },
    );

    const filteredLinks = recipe.links?.filter(l => l.trim() !== '') || [];

    return (
        <div className="fixed inset-0 bg-bg-cream dark:bg-slate-950 z-[200] flex flex-col font-rubik transition-colors duration-200">
            
            {/* Header: Full width, fixed top */}
            <div className="absolute top-0 left-0 w-full p-3 sm:p-4 z-50 flex flex-wrap gap-2 justify-between items-start pointer-events-none">
                <button 
                    onClick={onClose} 
                    className="pointer-events-auto shrink-0 max-w-[46%] sm:max-w-none px-4 sm:px-5 py-2 sm:py-2.5 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-all active:scale-95 border border-stone-200 dark:border-slate-800 font-black text-xs uppercase tracking-widest truncate"
                >
                    {t('close')}
                </button>

                <div className="pointer-events-auto flex flex-wrap justify-end gap-1.5 sm:gap-2 max-w-[52%] sm:max-w-none">
                    {onToggleFavoriteRecipe && (
                        <button
                            onClick={() => onToggleFavoriteRecipe(recipe.id)}
                            className={`p-2.5 sm:p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg transition-all active:scale-95 border border-stone-200 dark:border-slate-800 shrink-0 ${isFavoriteRecipe(userMealPreferences, recipe.id) ? 'text-rose-500 hover:text-rose-600' : 'text-slate-400 dark:text-slate-500 hover:text-rose-400'}`}
                            title={isFavoriteRecipe(userMealPreferences, recipe.id) ? t('unfavorite') : t('favorite')}
                        >
                            <HeartIcon className="w-5 h-5 sm:w-6 sm:h-6" filled={isFavoriteRecipe(userMealPreferences, recipe.id)} />
                        </button>
                    )}
                    {!guidedMode && (
                        <button 
                            onClick={() => setIsExportOpen(true)}
                            className="p-2.5 sm:p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg text-slate-400 dark:text-slate-500 hover:text-sky-600 dark:hover:text-sky-400 transition-all active:scale-95 border border-stone-200 dark:border-slate-800 shrink-0"
                            title={t('exportRecipe')}
                        >
                            <ShareIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    )}

                    {canManage && (
                        <>
                            {showDuplicateForHousehold && onDuplicateForHousehold && (
                                <button
                                    onClick={onDuplicateForHousehold}
                                    className="p-2.5 sm:p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg text-slate-400 dark:text-slate-500 hover:text-sky-600 dark:hover:text-sky-400 transition-all active:scale-95 border border-stone-200 dark:border-slate-800 shrink-0"
                                    title={t('duplicateForHousehold')}
                                >
                                    <ShareIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                                </button>
                            )}
                            <button 
                                onClick={() => setIsDeleteConfirmOpen(true)}
                                className="p-2.5 sm:p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all active:scale-95 border border-stone-200 dark:border-slate-800 shrink-0"
                                title={t('deleteRecipe')}
                            >
                                <TrashIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                            <button 
                                onClick={onEdit} 
                                className="p-2.5 sm:p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-full shadow-lg text-slate-400 dark:text-slate-500 hover:text-brand-sage dark:hover:text-brand-sage transition-all active:scale-95 border border-stone-200 dark:border-slate-800 shrink-0"
                                title={t('editRecipe')}
                            >
                                <PencilIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-grow overflow-y-auto scrollbar-none">
                <div className={`max-w-2xl mx-auto px-4 pb-40 pt-20`}>
                    
                    {/* Hero Section */}
                    <div className="bg-white dark:bg-slate-900 rounded-b-[3rem] shadow-sm border-x border-b border-stone-100 dark:border-slate-800 mb-8 animate-fade-in-down transition-colors overflow-hidden">

                        {/* Image or Icon Banner */}
                        {galleryImageUrls.length > 0 ? (
                            <RecipeImageGallery urls={galleryImageUrls} alt={displayRecipe.name} language={language} variant="hero" />
                        ) : (
                            <div className={`w-full flex justify-center pt-10 pb-6 ${meatStats.totalGrams > 0 ? 'bg-rose-50 dark:bg-rose-900/20' : 'bg-brand-sage/5 dark:bg-brand-sage/10'}`}>
                                <div className={`p-6 rounded-[2.5rem] shadow-lg ${meatStats.totalGrams > 0 ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-500 dark:text-rose-400' : 'bg-white dark:bg-slate-800 text-brand-sage'}`}>
                                    {meatStats.totalGrams > 0 ? <HamIcon className="w-16 h-16" /> : <LeafIcon className="w-16 h-16" />}
                                </div>
                            </div>
                        )}

                        {creditedChef?.imageUrl?.trim() && onViewChef && (
                            <div className="flex justify-center relative z-10 -mt-8 mb-1">
                                <button
                                    type="button"
                                    onClick={() => onViewChef(creditedChef.id)}
                                    className="rounded-full ring-4 ring-white dark:ring-slate-900 shadow-lg hover:scale-105 active:scale-95 transition-transform"
                                    title={getChefDisplayName(creditedChef, language || 'en')}
                                >
                                    <ChefAvatar
                                        imageUrl={creditedChef.imageUrl}
                                        name={getChefDisplayName(creditedChef, language || 'en')}
                                        size="md"
                                        className="!rounded-full !w-16 !h-16"
                                    />
                                </button>
                            </div>
                        )}

                        <div className={`px-8 pb-8 text-center ${creditedChef?.imageUrl?.trim() ? 'pt-2' : 'pt-5'}`}>
                            {/* Category badge */}
                            {recipe.category && (
                                <div className="flex justify-center mb-3">
                                    <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                        {t(recipe.category.toLowerCase())}
                                    </span>
                                </div>
                            )}

                            {/* Name */}
                            <h2 className="text-2xl sm:text-4xl font-nunito font-black text-brand-sage tracking-tight mb-2 break-words">
                                {displayRecipe.name}
                            </h2>

                            {/* Author + source */}
                            <div className="flex items-center justify-center gap-1.5 text-sm text-slate-400 dark:text-slate-500 mb-5">
                                <span>{authorName}</span>
                                {sourceDomain && recipe.links?.[0] && (() => {
                                    const sourceHref = sanitizeExternalHref(recipe.links[0]);
                                    if (!sourceHref) return null;
                                    return (
                                    <>
                                        <span>·</span>
                                        <a
                                            href={sourceHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sky-500 dark:text-sky-400 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {sourceDomain}
                                        </a>
                                    </>
                                    );
                                })()}
                            </div>

                            {/* Lifestyle + diet tags */}
                            {recipeTags.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-2 mb-5">
                                    {recipeTags.map(({ key, labelKey, emoji }) => (
                                        <span
                                            key={key}
                                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700 rounded-full text-[11px] font-bold"
                                        >
                                            <span>{emoji}</span>
                                            {t(labelKey)}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Stats */}
                            <div className="flex flex-wrap justify-center items-center gap-3 mb-6">
                                <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full text-xs font-black text-slate-500 dark:text-slate-400">
                                    <ClockIcon className="w-4 h-4" />
                                    <span>{displayRecipe.prepTime || 20} {t('mins')}</span>
                                </div>
                                
                                <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full">
                                    <UsersIcon className="w-4 h-4 text-slate-400 dark:text-slate-500 ml-2" />
                                    <SmartUnitCapsule 
                                        value={currentServings} 
                                        unit="servings" 
                                        onChange={(val) => setCurrentServings(val)}
                                        language={language}
                                        availableUnits={['servings']}
                                        step={1}
                                        min={1}
                                    />
                                </div>

                                <div className="flex items-center gap-1.5 px-4 py-2 bg-white dark:bg-slate-900 border border-amber-100 dark:border-amber-900/30 rounded-full">
                                    <StarRating rating={userRating || 0} onRate={handleRate} size="sm" />
                                </div>
                            </div>

                            {/* Schedule CTA */}
                            {onOpenAddMeal && (
                                <button 
                                    onClick={onOpenAddMeal}
                                    className="inline-flex items-center gap-2 px-8 py-3 bg-brand-orange text-white rounded-2xl shadow-lg shadow-brand-orange/20 font-black text-xs uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all"
                                >
                                    <CalendarPlusIcon className="w-4 h-4" />
                                    {t('schedule')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Ingredients Section */}
                    <div className="mb-14 space-y-6">
                        <div className="flex items-center justify-between px-2">
                            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight flex items-center gap-2">
                                 {t('ingredients')}
                            </h3>
                            <div className="flex items-center gap-2">
                                {isSelectionMode && (
                                    <button
                                        onClick={toggleSelectAll}
                                        className="text-[10px] font-black uppercase tracking-widest text-brand-orange hover:text-orange-700 dark:hover:text-orange-300 transition-colors px-2 py-1"
                                    >
                                        {allIngredientsSelected ? t('deselectAll') : t('selectAll')}
                                    </button>
                                )}
                                <button 
                                    onClick={toggleSelectionMode}
                                    className={`p-2.5 rounded-full transition-all shadow-sm ${isSelectionMode ? 'bg-orange-50 dark:bg-orange-900/30 text-brand-orange ring-2 ring-brand-orange/20' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                                    title={isSelectionMode ? t('cancel') : t('shoppingList')}
                                >
                                    {isSelectionMode ? <XIcon className="w-5 h-5" /> : <CartIcon className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        
                        {groupedIngredients.map(([section, ings]) => {
                            const isCollapsed = collapsedIngSections.has(section);
                            const translatedSection = t(section.toLowerCase());
                            const displaySection = translatedSection !== section.toLowerCase() ? translatedSection : section;
                            
                            return (
                                <div key={section} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300">
                                    <div 
                                        className="flex justify-between items-center p-5 cursor-pointer group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                        onClick={() => toggleIngSection(section)}
                                    >
                                        <div className="flex items-center gap-3 flex-grow min-w-0" onClick={e => e.stopPropagation()}>
                                            <div className={`transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => toggleIngSection(section)}>
                                                <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                            </div>
                                            <h4 className="text-xs font-black text-brand-orange uppercase tracking-[0.2em] truncate" onClick={() => toggleIngSection(section)}>{displaySection}</h4>
                                        </div>
                                        {isSelectionMode && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleSelectSection(ings); }}
                                                className="shrink-0 text-[9px] font-black uppercase tracking-widest text-brand-orange hover:text-orange-700 dark:hover:text-orange-300 transition-colors px-2 py-1"
                                            >
                                                {isSectionFullySelected(ings) ? t('deselectAll') : t('selectAll')}
                                            </button>
                                        )}
                                    </div>
                                    
                                    {!isCollapsed && (
                                        <div className="border-t border-slate-50 dark:border-slate-800 animate-fade-in divide-y divide-slate-50 dark:divide-slate-800">
                                            {ings.map((ing, i) => {
                                                const isStruck = struckIngredients.has(ing.name);
                                                const isSelected = selectedIngredients.has(ing.name);
                                                const scalingFactor = currentServings / (recipe.servings || 1);
                                                const displayedQuantity = ing.quantity * scalingFactor;
                                                
                                                let rowClass = "flex items-start gap-3 p-4 transition-all cursor-pointer ";
                                                if (ing.isMeat) rowClass += "bg-rose-50/30 dark:bg-rose-900/20 border-l-4 border-l-rose-500 dark:border-l-rose-600 ";
                                                else rowClass += "hover:bg-slate-50/50 dark:hover:bg-slate-800/50 ";

                                                return (
                                                    <div 
                                                        key={i} 
                                                        className={rowClass}
                                                        onClick={() => isSelectionMode ? toggleSelection(ing.name) : toggleStrike(ing.name)}
                                                    >
                                                        <div className={`
                                                            flex items-center justify-center transition-all duration-300 flex-shrink-0 mt-0.5
                                                            ${isSelectionMode 
                                                                ? `w-6 h-6 rounded border-2 ${isSelected ? 'bg-brand-orange border-brand-orange text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'}` 
                                                                : `w-6 h-6 rounded-full border-2 ${isStruck ? 'bg-brand-sage border-brand-sage text-white' : 'border-stone-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-inner'}`
                                                            }
                                                        `}>
                                                            {isSelectionMode 
                                                                ? (isSelected && <CheckIcon className="w-4 h-4" />)
                                                                : (isStruck && <CheckIcon className="w-4 h-4" />)
                                                            }
                                                        </div>

                                                        <div className="flex-1 min-w-0">
                                                            <span className={`text-lg font-bold tracking-tight transition-all break-words leading-snug ${!isSelectionMode && isStruck ? 'line-through text-slate-300 dark:text-slate-600' : (ing.isMeat ? 'text-rose-900 dark:text-rose-200' : 'text-slate-700 dark:text-slate-200')}`}>
                                                                {ing.name}
                                                            </span>
                                                            {ing.isMeat && (
                                                                <span className={`block mt-0.5 text-[9px] font-black uppercase tracking-widest ${isStruck && !isSelectionMode ? 'text-slate-300 dark:text-slate-600' : 'text-rose-400 dark:text-rose-400'}`}>
                                                                    {t('butcherItem')}
                                                                </span>
                                                            )}
                                                            {ing.alternatives && ing.alternatives.length > 0 && (
                                                                <div className="mt-1.5 space-y-0.5">
                                                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                                                        {t('ingredientOptions')}
                                                                    </span>
                                                                    {ing.alternatives.map((alt, altIdx) => (
                                                                        <p
                                                                            key={altIdx}
                                                                            className={`text-xs font-medium ${isStruck && !isSelectionMode ? 'text-slate-300 dark:text-slate-600 line-through' : 'text-slate-500 dark:text-slate-400'}`}
                                                                        >
                                                                            {alt.name} — <CookingQuantityCapsule quantity={alt.quantity * scalingFactor} unit={alt.unit} t={t} rtl={rtl} variant="inline" struck={isStruck && !isSelectionMode} />
                                                                        </p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <CookingQuantityCapsule
                                                            quantity={displayedQuantity}
                                                            unit={ing.unit}
                                                            t={t}
                                                            rtl={rtl}
                                                            isMeat={ing.isMeat}
                                                            struck={isStruck && !isSelectionMode}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Instructions Section */}
                    <div className="mb-10 space-y-6">
                        <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-tight px-2 flex items-center gap-2">
                             <ClockIcon className="w-6 h-6 text-brand-sage" />
                             {t('instructions')}
                        </h3>
                        
                        {groupedInstructions.map((section, sIdx) => {
                            const isCollapsed = collapsedPrepSections.has(section.header);
                            const translatedHeader = t(section.header.toLowerCase());
                            const displayHeader = translatedHeader !== section.header.toLowerCase() ? translatedHeader : section.header;
                            const sectionDoneCount = section.steps.filter((_, idx) => completedSteps.has(stepKey(sIdx, idx))).length;
                            const sectionComplete = section.steps.length > 0 && sectionDoneCount === section.steps.length;

                            return (
                                <div key={section.header} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300">
                                    <div 
                                        className="flex justify-between items-center p-5 cursor-pointer group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                        onClick={() => togglePrepSection(section.header)}
                                    >
                                        <div className="flex items-center gap-3 flex-grow min-w-0" onClick={e => e.stopPropagation()}>
                                            <div className={`transition-transform duration-300 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => togglePrepSection(section.header)}>
                                                <ChevronDownIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                            </div>
                                            <h4 className="text-xs font-black text-brand-sage uppercase tracking-[0.2em] truncate" onClick={() => togglePrepSection(section.header)}>{displayHeader}</h4>
                                        </div>
                                        <span className={`shrink-0 text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full border transition-colors ${sectionComplete ? 'bg-brand-sage/10 text-brand-sage border-brand-sage/20' : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-100 dark:border-slate-700'}`}>
                                            {sectionDoneCount}/{section.steps.length}
                                        </span>
                                    </div>
                                    
                                    {!isCollapsed && (
                                        <div className="border-t border-slate-50 dark:border-slate-800 animate-fade-in p-6 space-y-8">
                                            {section.steps.map((step, idx) => {
                                                const stepIngredients = getStepIngredients(step.content);
                                                const key = stepKey(sIdx, idx);
                                                const isDone = completedSteps.has(key);
                                                return (
                                                    <div key={step.id || idx} className="flex gap-6 items-start group">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleStepComplete(key)}
                                                            title={isDone ? t('markIncomplete') : t('markComplete')}
                                                            className={`flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center font-black text-lg border shadow-sm transition-all active:scale-95 ${isDone ? 'bg-brand-sage border-brand-sage text-white' : 'bg-brand-sage/5 dark:bg-brand-sage/10 text-brand-sage border-brand-sage/10 hover:bg-brand-sage/10 dark:hover:bg-brand-sage/20'}`}
                                                        >
                                                            {isDone ? <CheckIcon className="w-5 h-5" /> : (idx + 1)}
                                                        </button>
                                                        <div className="flex-grow">
                                                            <p className={`text-xl leading-relaxed font-medium pt-1 transition-all ${isDone ? 'line-through text-slate-300 dark:text-slate-600' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                {step.content}
                                                            </p>
                                                            {stepIngredients.length > 0 && (
                                                                <div className={`flex flex-wrap gap-2 mt-3 transition-opacity ${isDone ? 'opacity-40' : ''}`}>
                                                                    {stepIngredients.map(ing => {
                                                                         const scalingFactor = currentServings / (recipe.servings || 1);
                                                                         const qty = ing.quantity * scalingFactor;
                                                                         return (
                                                                             <div key={ing.name} className="inline-flex items-center px-2.5 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/50 text-orange-700 dark:text-orange-300 text-xs font-bold">
                                                                                <span className="opacity-70 ltr:mr-1 rtl:ml-1">{ing.name}:</span>
                                                                                <CookingQuantityCapsule quantity={qty} unit={ing.unit} t={t} rtl={rtl} variant="inline" struck={isDone} />
                                                                             </div>
                                                                         );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {displayRecipe.nutritionFactsPer100g && (
                        <div className="mb-10 px-2">
                            <NutritionFactsPanel
                                facts={displayRecipe.nutritionFactsPer100g}
                                language={language}
                                collapsed={isNutritionCollapsed}
                                onToggleCollapse={() => setIsNutritionCollapsed((prev) => !prev)}
                            />
                        </div>
                    )}

                    {(creditedChef || recipe.originNote) && (
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-[2.5rem] p-8 border border-stone-100 dark:border-slate-800 mb-8">
                            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4 px-1">{t('creditsAndSources')}</h4>
                            {creditedChef && (
                                <button
                                    type="button"
                                    onClick={() => onViewChef?.(creditedChef.id)}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 mb-3 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-xl text-brand-sage font-bold text-sm hover:shadow-md transition-all"
                                >
                                    <ChefHatIcon className="w-4 h-4" />
                                    <span>{getChefDisplayName(creditedChef, language || 'en')}</span>
                                </button>
                            )}
                            {recipe.originNote && (
                                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed italic px-1">{recipe.originNote}</p>
                            )}
                        </div>
                    )}

                    {filteredLinks.length > 0 && (
                        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-[2.5rem] p-8 border border-stone-100 dark:border-slate-800 mb-8">
                            <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4 px-1">{t('links')}</h4>
                            <div className="flex flex-wrap gap-2">
                                {filteredLinks.map((link, idx) => {
                                    const safeHref = sanitizeExternalHref(link);
                                    return (
                                    <div
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1.5 bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-xl hover:shadow-md transition-all"
                                    >
                                        {safeHref ? (
                                            <a
                                                href={safeHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-3 px-2 py-1 text-sky-600 dark:text-sky-400 font-bold text-sm"
                                            >
                                                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                <span className="truncate max-w-[180px]">{getDomain(link)}</span>
                                            </a>
                                        ) : (
                                            <span className="inline-flex items-center gap-3 px-2 py-1 text-slate-500 dark:text-slate-400 font-bold text-sm">
                                                <span className="truncate max-w-[180px]">{getDomain(link)}</span>
                                            </span>
                                        )}
                                        <CopyButton
                                            text={link}
                                            copyLabel={t('copyLink')}
                                            copiedLabel={t('copied')}
                                        />
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Selection Bar - OUTSIDE scroll area */}
            {isSelectionMode && (
                <div className="absolute bottom-6 left-4 right-4 bg-slate-900 dark:bg-slate-800 text-white p-4 rounded-2xl shadow-2xl flex justify-between items-center z-[210] animate-fade-in border border-slate-700">
                    <span className="font-bold text-sm px-2">{selectedIngredients.size} {t('selected')}</span>
                    <div className="flex gap-3">
                        <button 
                            onClick={toggleSelectionMode} 
                            className="px-4 py-2 text-slate-400 font-bold text-xs uppercase tracking-widest hover:text-white transition-colors"
                        >
                            {t('cancel')}
                        </button>
                        <button 
                            onClick={handleAddToCart}
                            disabled={isAddingToCart || selectedIngredients.size === 0}
                            className="bg-brand-orange text-white px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-orange/20 active:scale-95 transition-all disabled:opacity-50 disabled:shadow-none flex items-center justify-center gap-2"
                        >
                            {isAddingToCart ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <CartIcon className="w-4 h-4" />
                            )}
                            {t('addToActive')}
                        </button>
                    </div>
                </div>
            )}

            <RecipeExportModal 
                isOpen={isExportOpen} 
                onClose={() => setIsExportOpen(false)} 
                recipe={displayRecipe}
                language={language}
            />

            <ConfirmationModal 
                isOpen={isDeleteConfirmOpen} 
                title={t('deleteRecipe')} 
                message={`${t('deleteRecipe')}? "${recipe.name}"`}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                onConfirm={() => {
                    onDelete();
                    setIsDeleteConfirmOpen(false);
                }}
                onCancel={() => setIsDeleteConfirmOpen(false)}
            />
        </div>
    );
};

export default RecipeDetailsModal;
